import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useAuth } from "../../app/providers/AuthProvider";
import { useInactivityLogout } from "../../hooks/useInactivityLogout";
import { InactivityWarningModal } from "./InactivityWarningModal";

/**
 * Wraps authenticated pages to enforce a client-side inactivity timeout.
 * The warning modal appears at (timeoutMs - warningMs) idle; if the user
 * doesn't click "Stay signed in" within `warningMs`, they're logged out.
 */
export function InactivityGuard({ children }) {
  const { status, clearSession } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onExpire = useCallback(() => {
    clearSession();
    toast.error(t("session.expired_flash"), { id: "inactivity-flash" });
    navigate("/login", { replace: true });
  }, [clearSession, navigate, t]);

  const { warningActive, remainingMs, stayLoggedIn, expireNow } =
    useInactivityLogout({
      enabled: status === "authenticated",
      onExpire,
    });

  return (
    <>
      {children}
      <InactivityWarningModal
        open={warningActive}
        remainingMs={remainingMs}
        onStay={stayLoggedIn}
        onLogout={expireNow}
      />
    </>
  );
}
